/* eslint-disable jsx-a11y/label-has-associated-control */
import { DetailedHTMLProps, InputHTMLAttributes, useEffect, useMemo } from 'react';

import clsx from 'clsx';
import { useRouter } from 'next/router';
import { useUnfortunateWayToAppendCurrency } from '@portal/state/hooks/useUnfortunateWayToAppendCurrency';
import { useStoreCurrency } from 'state/hooks/useStoreCurrency';

import { useIsLoggedInValue, useSessionValue } from 'appLayer/auth';
import { useQueryGetSubscriptionsV2 } from 'state/internal/getSubscriptions';
import { AssurancesBaB } from '@portal/shared/components/AssurancesBaB/AssurancesBaB';
import Select from '@portal/shared/ui/Select';
import { useProductStore } from '@portal/state/store/pdpStore';
import { SanityKeyed } from '@portal/types/studio/studio';
import { loadShopifyProductVariantById } from '@portal/api/shopifyV2/variants';
import { useCartRuleDiscounts } from '@portal/shared/hooks/useCartRuleDiscounts';
import { useStoreValue } from '@portal/state/hooks/useStore';
import React from 'react';
import { StudioHotspotImageV2 } from '@portal/shared/studio/StudioHotspotImage/StudioHotspotImage';
import Link from 'next/link';
import * as styles from './styles.css';

type CadenceOption = SanityKeyed<{
  _type: 'cadenceOption';
  label?: string;
  sellingPlans?: Array<
    SanityKeyed<{
      _type: 'sellingPlanItem';
      targetStore?: 'dev' | 'qa' | 'aud' | 'nzd' | 'eud' | 'ukd' | 'us' | 'au' | 'nz' | 'eu' | 'uk';
      sellingPlan?: string;
    }>
  >;
  queryParam?: string;
}>;

type CadenceOptions = Array<CadenceOption>;

interface Props extends DetailedHTMLProps<InputHTMLAttributes<HTMLInputElement>, HTMLInputElement> {
  title: string;
  titleMembers: string;
  variantId: string | undefined;
  price: string;
  pdpOldPrice?: string;
  assurances?: Array<SanityKeyed<string>>;
  oneTimeTitle?: string;
  oneTimePrice?: string;
  oneTimeAssurances?: Array<SanityKeyed<string>>;
  isOneTime: boolean;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  hasCadenceSelector?: boolean;
  cadenceOptions?: CadenceOptions;
  addSuperScript?: boolean;
  addSuperScriptLis?: boolean;
  hideStrikeThroughLIS?: boolean;
  oldPriceFirst: boolean;
  showGreenbar?: boolean;
  showGreenbarLIS?: boolean;
  showIcon?: boolean;
  icon?: any;
  greenbarLabel?: string;
  greenBarStaticURLLabel?: string;
  greenBarStaticURL?: string;
  defaultDeliveryCadenceAssurance?: string;
  pdpMemberOtpPrice?: string;
}

export const PriceInput = ({
  title = '',
  titleMembers,
  variantId,
  price,
  pdpOldPrice,
  assurances,
  oneTimeTitle,
  oneTimePrice,
  oneTimeAssurances,
  isOneTime,
  onChange,
  checked,
  hasCadenceSelector = false,
  cadenceOptions = [],
  addSuperScript = false,
  addSuperScriptLis = false,
  hideStrikeThroughLIS = false,
  oldPriceFirst = false,
  defaultDeliveryCadenceAssurance = '',
  showGreenbar = false,
  showGreenbarLIS = false,
  showIcon = false,
  icon,
  greenbarLabel,
  greenBarStaticURLLabel,
  greenBarStaticURL
}: Props) => {
  const router = useRouter();
  const store = useStoreValue();
  const { symbolCurrency } = useStoreCurrency();
  const session = useSessionValue();
  const isLoggedIn = useIsLoggedInValue();
  const { data: subscriptions = [], isLoading: isLoadingSubscriptions } =
    useQueryGetSubscriptionsV2();

  const { productCadence, setProductCadence, setIsOtpProduct } = useProductStore();

  const { conditionallyAppendUSDtoPrice, shouldAppendUSD } = useUnfortunateWayToAppendCurrency();

  // Fetch discounted variant info for subscription products only
  const { hasDiscount, discountedVariantId } = useCartRuleDiscounts(
    !isOneTime && variantId ? variantId : undefined
  );

  // State to store discounted price
  const [discountedPrice, setDiscountedPrice] = React.useState<string | null>(null);

  // Fetch discounted variant price if applicable
  useEffect(() => {
    const fetchDiscountedPrice = async () => {
      if (!hasDiscount || !discountedVariantId || !store) {
        setDiscountedPrice(null);
        return;
      }

      try {
        const variant = await loadShopifyProductVariantById(store, discountedVariantId);

        if (variant?.price?.amount) {
          // Format the price properly (remove unnecessary decimals)
          const price = parseFloat(variant.price.amount);
          setDiscountedPrice(Number.isInteger(price) ? price.toString() : variant.price.amount);
        } else {
          console.warn('No price found for discounted variant:', discountedVariantId);
          setDiscountedPrice(null);
        }
      } catch (error) {
        console.warn('Failed to fetch discounted price:', error);
        setDiscountedPrice(null);
      }
    };

    fetchDiscountedPrice();
  }, [hasDiscount, discountedVariantId, store]);

  // Use discounted price if available, otherwise use regular price
  const displayPrice = useMemo(() => {
    if (!isOneTime && discountedPrice && hasDiscount) {
      // Format the price to remove decimals if it's a whole number
      const numericPrice = parseFloat(discountedPrice);
      return Number.isInteger(numericPrice) ? numericPrice.toString() : discountedPrice;
    }
    return price;
  }, [isOneTime, discountedPrice, hasDiscount, price]);

  const formattedAssurances = isOneTime ? oneTimeAssurances : assurances;
  const radioId = `${variantId}-${isOneTime ? 'otp' : 'sub'}`;

  const priceScaled = displayPrice;
  const oldPriceScaled = pdpOldPrice;
  const oneTimePriceScaled = oneTimePrice;

  // When a discount is shown, the comparison price should also be the variant's
  // own price (not multiplied again).
  const actualOldPrice = hasDiscount && !isOneTime && oneTimePrice ? oneTimePrice : oldPriceScaled;
  const useMembersUI = Boolean(subscriptions?.length) && isLoggedIn;
  const designatedTitle = useMembersUI ? titleMembers : title;
  const isLoading = Boolean(session?.customer) && isLoadingSubscriptions;
  const membersAssurance = useMembersUI ? [defaultDeliveryCadenceAssurance] : formattedAssurances;

  useEffect(() => {
    if (cadenceOptions.length > 0) {
      const frequencyQueryParam = router?.query?.frequency;
      const matchingOption = cadenceOptions?.find(
        (option) => frequencyQueryParam === option?.queryParam
      );

      if (frequencyQueryParam && matchingOption && matchingOption.sellingPlans?.[0]?.sellingPlan) {
        setProductCadence(matchingOption.sellingPlans[0].sellingPlan || '');
      } else if (!productCadence && cadenceOptions[0]?.sellingPlans?.[0]?.sellingPlan) {
        setProductCadence(cadenceOptions[0].sellingPlans[0].sellingPlan || '');
      }
    }
  }, [cadenceOptions, productCadence, setProductCadence, router]);

  const handleChange = (value: string) => {
    setIsOtpProduct(false);
    setProductCadence(value as string);
    const newQuery = { ...router.query, cadence: value };

    router.replace(
      {
        pathname: router.pathname,
        query: newQuery
      },
      undefined,
      { shallow: true }
    );
  };

  if (isLoading) return null;

  const showGreenBanner = useMembersUI
    ? showGreenbar || showGreenbarLIS
    : showGreenbar && !showGreenbarLIS;

  return (
    <label
      htmlFor={radioId}
      className={clsx(styles.card, {
        [styles.cardOneTime]: isOneTime,
        [styles.cardChecked]: checked,
        [styles.cardCheckedOneTime]: checked && isOneTime
      })}>
      <input
        type="radio"
        name={designatedTitle}
        id={radioId}
        value={isOneTime ? 'otp' : 'sub'}
        className={styles.input}
        onChange={onChange}
        checked={checked}
      />
      {showGreenBanner && !isOneTime && (
        <div className={styles.greenbar}>
          <div className={styles.greenbarContent}>
            {showIcon && (
              <StudioHotspotImageV2
                image={icon}
                className={styles.greenbarIcon}
                eagerLoading={true}
              />
            )}
            <span className={styles.greenbarLabel}>{greenbarLabel}</span>
          </div>

          {(greenBarStaticURLLabel || greenBarStaticURL) && (
            <Link
              className={styles.greenBarStaticURLLabel}
              href={(greenBarStaticURL as string) || '/'}>
              {greenBarStaticURLLabel || ''}
            </Link>
          )}
        </div>
      )}
      <div className={clsx(styles.main, isOneTime ? styles.mainOneTime : styles.mainSubscription)}>
        {isOneTime && (
          <div className={clsx(styles.content, styles.contentOneTimeNew)}>
            <div className={styles.priceHeaderRow}>
              <div className={styles.checkmarkWrap}>
                <div
                  className={clsx(styles.checkmark, {
                    [styles.checkmarkChecked]: checked
                  })}
                />
              </div>
              <div className={clsx(styles.heading, styles.contentOneTimeNew)}>
                {oneTimeTitle && (
                  <span
                    className={clsx(styles.priceTitle, {
                      [styles.USDEnabledText]: shouldAppendUSD(oneTimePriceScaled)
                    })}>
                    {oneTimeTitle}
                  </span>
                )}
                <div className={styles.priceWrapper}>
                  <span
                    className={clsx(styles.priceValue, {
                      [styles.USDEnabledText]: shouldAppendUSD(oneTimePriceScaled)
                    })}>
                    {symbolCurrency}
                    {conditionallyAppendUSDtoPrice(oneTimePriceScaled)}
                    {useMembersUI && addSuperScriptLis && (
                      <sup className={styles.priceSuperBold}>†</sup>
                    )}
                  </span>
                </div>
              </div>
            </div>
            <AssurancesBaB
              isSubscription={false}
              assurances={formattedAssurances || []}
              className={styles.assurancesOtp}
              isPdp
            />
          </div>
        )}

        {!isOneTime && (
          <div className={styles.content}>
            <div className={styles.priceHeaderRow}>
              <div className={styles.checkmarkWrap}>
                <div
                  className={clsx(styles.checkmark, {
                    [styles.checkmarkChecked]: checked
                  })}
                />
              </div>
              <div className={clsx(styles.heading, styles.headingNewDesign)}>
                <span
                  className={clsx(styles.priceTitle, {
                    [styles.USDEnabledText]: shouldAppendUSD(priceScaled)
                  })}>
                  {designatedTitle}
                </span>
                <div className={clsx(styles.price, oldPriceFirst && styles.flexRowReverse)}>
                  <span
                    className={clsx(styles.priceValue, styles.priceValueNewDesign, {
                      [styles.USDEnabledText]: shouldAppendUSD(priceScaled)
                    })}>
                    {symbolCurrency}
                    {conditionallyAppendUSDtoPrice(priceScaled) as string}
                    {hasDiscount && discountedPrice && '/mo'}
                    {useMembersUI && addSuperScriptLis && (
                      <sup className={styles.priceSuperBold}>†</sup>
                    )}
                  </span>
                  {!(useMembersUI && hideStrikeThroughLIS) && actualOldPrice && (
                    <span>
                      <span
                        aria-label="original price struck through"
                        className={clsx(styles.priceValue, styles.priceValueOld, {
                          [styles.USDEnabledText]: shouldAppendUSD(actualOldPrice)
                        })}>
                        {symbolCurrency}
                        {conditionallyAppendUSDtoPrice(actualOldPrice)}
                      </span>
                      {addSuperScript && <sup className={styles.priceSuper}>†</sup>}
                    </span>
                  )}
                </div>
              </div>
            </div>
            {!useMembersUI && hasCadenceSelector && cadenceOptions && (
              <div className={styles.cadenceSelector}>
                <label className={styles.cadenceSelectorLabel}>
                  <Select
                    dense
                    className={styles.cadenceSelect}
                    value={
                      router.query.cadence
                        ? router.query.cadence
                        : productCadence || cadenceOptions[0]?.sellingPlans?.[0]?.sellingPlan || ''
                    }
                    isSearchable={false}
                    options={cadenceOptions.map((option) => ({
                      value: option.sellingPlans?.[0]?.sellingPlan || '',
                      label: option.label || ''
                    }))}
                    onChange={handleChange}
                    aria-label="Select cadence"
                  />
                </label>
              </div>
            )}
            <div
              className={clsx(styles.assurancesWrapper, {
                [styles.noBorder]: hasCadenceSelector
              })}>
              <AssurancesBaB assurances={membersAssurance || []} className={styles.assurances} />
            </div>
          </div>
        )}
      </div>
    </label>
  );
};
